/**
 * A SCREEN IS DRAWN TWICE, AND NEVER LEARNS WHICH.
 *
 * Every branch chooses `browser` (the layout every screen is drawn in, and the
 * default) or `grid` (operational screens as tables, tighter geometry). The
 * mechanism is an attribute and a token block, not a conditional:
 * `<html data-view="grid">` and `:root[data-view="grid"]` in styles/index.css
 * re-declare the same token names, so a screen that reads tokens is already
 * right in both. `ViewProvider` (view-provider.tsx) paints the attribute.
 *
 * `useView()` exists for the few components that change SHAPE, not size (a list
 * that becomes a DataGrid, a field that becomes a row). If a call site needs
 * `view === "grid" ? … : …` for a size, a token is missing.
 */
import { createContext, useContext } from "react";
import type { ScreenLayout } from "@/api/preference";

export type View = "browser" | "grid";

export function readView(value: unknown): View {
  return value === "grid" ? "grid" : "browser";
}

/**
 * THE CONTRACT'S WORD FOR THIS, WHICH IS NOT OURS.
 *
 * `UserObj.screenLayout` is `BROWSER` or `WINGRID` (*PharmOS API* 1.0.0,
 * 21-09-2026). This repo has said `browser` and `grid` since the first token
 * block, they are in `<html data-view>`, in `:root[data-view="grid"]`, in the
 * e2e project names and in every comment — so the translation happens HERE,
 * once, and nothing downstream learns the server's spelling.
 *
 * `WINGRID` is the better name for what it is (the layout exists because the
 * staff who ask for it are describing the Windows grid software they came
 * from) and `grid` is the one already written down about a hundred times. The
 * cost of renaming is a hundred comments; the cost of two spellings is one
 * function, so it is one function.
 *
 * ANYTHING UNRECOGNISED IS `browser`, deliberately: it is the layout every
 * screen was drawn in, and a value nobody planned for should land a reader on
 * the one that is always right rather than on the tighter one.
 */
export function viewFromScreenLayout(value: string | null | undefined): View {
  return value?.trim().toUpperCase() === "WINGRID" ? "grid" : "browser";
}

/** The other direction, for `PUT /v1/user/preference`. */
export function screenLayoutOf(view: View): ScreenLayout {
  return view === "grid" ? "WINGRID" : "BROWSER";
}

export const ViewContext = createContext<View>("browser");

export function useView(): View {
  return useContext(ViewContext);
}
